import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Sentry from '@sentry/react-native';
import * as amplitude from '@amplitude/analytics-browser';

import { Environment } from '@/env';
import { useAnalytics } from '@/hooks/use-analytics';

const { AMPLITUDE_API_KEY } = Environment;

export const AmplitudeContext = createContext({});

export const AmplitudeContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { listenForUtmSource } = useAnalytics();
  const amplitudeRef = useRef<any>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const initializeAmplitude = async () => {
      try {
        await amplitude.init(AMPLITUDE_API_KEY, undefined, {
          defaultTracking: {
            sessions: true,
          },
        }).promise;

        amplitudeRef.current = amplitude;
        setIsReady(true);

        const searchParams = new URLSearchParams(window.location.search);
        const utmSource = searchParams.get('utm_source');
        if (utmSource) {
          amplitude.track('utm_source_open', {
            utm_source: utmSource,
          });
        }
      } catch (error) {
        console.error('Amplitude initialization failed:', error);
        Sentry.captureException(error, {
          tags: { component: 'AmplitudeProvider' },
          contexts: {
            amplitude: {
              api_key_present: !!AMPLITUDE_API_KEY,
              error_type: 'amplitude_init_error',
            },
          },
        });
      }
    };

    initializeAmplitude();
  }, []);

  useEffect(() => {
    if (isReady) {
      listenForUtmSource();
    }
  }, [isReady, listenForUtmSource]);

  const trackAmplitudeEvent = useCallback(
    (eventName: string, eventProperties: Record<string, unknown>) => {
      amplitudeRef.current?.track?.(eventName, eventProperties);
    },
    []
  );

  const value = useMemo(() => ({ trackAmplitudeEvent }), [trackAmplitudeEvent]);

  return (
    <AmplitudeContext.Provider value={value}>
      {children}
    </AmplitudeContext.Provider>
  );
};
